/**
 * Shared reading of a filter option's label and its count.
 *
 * A count belongs beside an option, not inside its text: it is a different
 * fact about a different thing, it is compared down a column, and a label that
 * carries it cannot be echoed anywhere else without carrying it too. Controls
 * the backend publishes hand the count over separately; a page that still
 * builds its own options may spell it into the label as a `(3) ` prefix, so
 * that shape is read back here and rendered the same way rather than shown
 * twice or shown as text.
 *
 * Contains:
 * - splitOptionCount — an option → its plain label and its count
 * - stripCountPrefix — a label with any legacy count prefix removed
 */

/** Matches the `(12) ` a page-built option label may still lead with. */
const COUNT_PREFIX = /^\((\d+)\)\s*/;

/**
 * Strips the leading "(count) " prefix some option labels carry. Option
 * labels may be JSX nodes (e.g. a page's own dot + text rows), which pass
 * through untouched — a label is only ever rendered as a React child.
 *
 * @param {string|React.ReactNode} raw - The option's `label`.
 * @returns {string|React.ReactNode} The label with any count prefix stripped.
 */
export function stripCountPrefix(raw) {
  return typeof raw === 'string' ? raw.replace(COUNT_PREFIX, '') : raw;
}

/**
 * Read an option as a label and a count, whichever way it carries them.
 *
 * @param {{label: (string|React.ReactNode), value: *, count: (number|undefined)}} option
 * @returns {{label: (string|React.ReactNode), count: (number|undefined)}}
 *   `count` is undefined when the option names none — which is not the same as
 *   zero, and reads as no number at all rather than as "(0)".
 */
export function splitOptionCount(option) {
  const raw = option?.label ?? String(option?.value ?? '');
  if (typeof option?.count === 'number') return { label: stripCountPrefix(raw), count: option.count };
  const match = typeof raw === 'string' ? raw.match(COUNT_PREFIX) : null;
  if (!match) return { label: raw, count: undefined };
  return { label: raw.slice(match[0].length), count: Number(match[1]) };
}
